/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { useCallback, useEffect, useMemo, useState } from "react";

import { PlayerMediaState } from "../../../../../../shared/types/media";
import { usePlayerCurrentMedia } from "../../../../../Controller";
import { useCustomPlayerContext } from "../../../../../CustomPlayerContext";
import { useActiveSection } from "../../../../Base";

const emptyState: PlayerMediaState = {};

export const useSeason = () => {
  const { media } = usePlayerCurrentMedia();
  const { seasonId } = useActiveSection();
  const { onFetchMedia } = useCustomPlayerContext();

  const [parentMedia, setParentMedia] = useState<PlayerMediaState>(emptyState);
  const [seasonMedia, setSeasonMedia] = useState<PlayerMediaState>(emptyState);
  const [seriesMedia, setSeriesMedia] = useState<PlayerMediaState>(emptyState);

  const fetchMedia = useCallback(
    async (mediaId: number | string) => {
      if (!onFetchMedia) return undefined;
      try {
        return await onFetchMedia(mediaId);
      } catch {
        return undefined;
      }
    },
    [onFetchMedia],
  );

  useEffect(() => {
    if (media?.ParentMediaId && !parentMedia.Media) {
      setParentMedia((prev) => ({ ...prev, IsLoading: true }));
      fetchMedia(media.ParentMediaId).then((result) => {
        setParentMedia({ Media: result, IsLoading: false });
      });
    }
  }, [media?.ParentMediaId]);

  useEffect(() => {
    if (seasonId && !seasonMedia.Media) {
      setSeasonMedia((prev) => ({ ...prev, IsLoading: true }));
      fetchMedia(seasonId).then((result) => {
        setSeasonMedia({ Media: result, IsLoading: false });
      });
    }
  }, [seasonId]);

  useEffect(() => {
    if (parentMedia.Media?.ParentMediaId && !seriesMedia.Media) {
      setSeriesMedia((prev) => ({ ...prev, IsLoading: true }));
      fetchMedia(parentMedia.Media.ParentMediaId).then((result) => {
        setSeriesMedia({ Media: result, IsLoading: false });
      });
    }
  }, [parentMedia.Media?.ParentMediaId]);

  const activeSeason = useMemo(() => {
    return media.ParentMediaId;
  }, [media]);

  return {
    seasonMedia,
    seriesMedia,
    parentMedia,
    activeSeason,
  };
};
